import { useAuth } from '@/shared/lib/auth/auth-context'
import type { OwnRole } from '@/shared/lib/auth/auth-provider'
import { usePermissions } from '@/shared/lib/permissions/use-permissions'
import { Permissions, PermissionLevel } from '@/shared/lib/permissions/permission-values'
import { useRolesQuery } from '../api/use-roles-query'

interface MyRoles {
  /** Null when nothing can name the roles: the dialog keeps the role IDs (UC-084.9 E2). */
  roles: OwnRole[] | null
  isLoading: boolean
}

/**
 * The caller's own roles with their grants (UC-084.9 step 3): the identity
 * `RoleDetails` when saas sends it, else `api/roles` for a UserManagement
 * reader, else nothing — so a standard user never fires a refused request.
 * `active` keeps the request off until the dialog opens.
 */
export function useMyRoles(active: boolean): MyRoles {
  const { user } = useAuth()
  const { can } = usePermissions()
  const fromIdentity = user?.roleDetails
  const canReadRoles = can({ name: Permissions.UserManagement, level: PermissionLevel.Read })
  const { roles: tenantRoles, isLoading } = useRolesQuery({ enabled: active && !fromIdentity && canReadRoles })

  if (fromIdentity) return { roles: fromIdentity, isLoading: false }
  if (!user || !canReadRoles) return { roles: null, isLoading: false }

  const roles = user.roles.map((roleId): OwnRole => {
    const role = tenantRoles.find((tenantRole) => String(tenantRole.Id) === roleId)
    return role
      ? { id: role.Id, name: role.Name, isPreset: role.IsPreset, modules: role.Modules }
      : { id: Number(roleId), name: `#${roleId}`, isPreset: true, modules: [] }
  })
  return { roles, isLoading }
}
